import React, { useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { Checkbox } from 'ncoded-component-library';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import ReorderResizeTable from 'components/ReorderResizeTable';
import useSelect from 'hooks/useSelect';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DataRoomRFICategory,
  DATA_ROOM_RFI_UPLOAD_STATUS,
} from 'router/subrouters/DataRoom/models/DataRoomRfi';
import RfiCategoriesActionMenu from './components/RfiCategoriesActionMenu';
import RfiReassignCategoriesModal from './components/RfiReassignCategoriesModal';
import {
  TABLE_COLUMNS,
  TABLE_COLUMNS_WITHOUT_CHECKBOX,
  HEADING_TRANSLATIONS,
  SORTING_VALUES,
  CUSTOM_COL_SIZES,
  BODY_CONTENT_GENERATE,
  RfiSpreadsheetCategoryTableRow,
} from './data';

type RfiSpreadsheetCategoriesTableProps = {
  className?: string;
  isLoading?: boolean;
  data: DataRoomRFICategory[];
  totalPages?: number;
  totalItems?: number;
  assignedEmails: string[];
  isReassignEnabled: boolean;
  onCategoryUpdate: (category: DataRoomRFICategory) => void;
  onRowClick: (category: DataRoomRFICategory) => void;
};

const RfiSpreadsheetCategoriesTable: React.FC<
  RfiSpreadsheetCategoriesTableProps
> = (props) => {
  const {
    className,
    isLoading,
    data,
    totalPages,
    totalItems,
    assignedEmails,
    isReassignEnabled,
    onCategoryUpdate,
    onRowClick,
  } = props;

  const [reassignCategories, setReassignCategories] = useState<
    DataRoomRFICategory[]
  >([]);
  const [reassignModal, reassignModalRef] = useCallbackRef<ModalRef>();

  const selectableCategories = useMemo(
    () =>
      data.filter(
        (category) =>
          category.uploadStatus !== DATA_ROOM_RFI_UPLOAD_STATUS.UPLOADED,
      ),
    [data],
  );

  const {
    selected,
    isAllSelected,
    isSelectedMode,
    isShiftPressed,
    selectAll,
    removeAll,
    updateSelected,
    onSelect,
  } = useSelect(selectableCategories);

  const onReassignClick = useCallback(
    (categories: DataRoomRFICategory[]) => {
      setReassignCategories(categories);
      reassignModal?.open();
    },
    [reassignModal],
  );

  const classes = classNames('rfi-spreadsheet-categories-table', className);

  const tableColumns = useMemo(
    () => (isReassignEnabled ? TABLE_COLUMNS : TABLE_COLUMNS_WITHOUT_CHECKBOX),
    [isReassignEnabled],
  );

  const tableContentTransformations = useMemo(
    () =>
      TABLE_COLUMNS.reduce(
        (acc, col) => ({
          ...acc,
          [col]: (category: DataRoomRFICategory) =>
            BODY_CONTENT_GENERATE[col]({
              category,
              isSelected: selected.some(({ _id }) => _id === category._id),
              isSelectedMode,
              isUploaded:
                category.uploadStatus === DATA_ROOM_RFI_UPLOAD_STATUS.UPLOADED,
              isReassignEnabled,
              onSelect: isShiftPressed ? onSelect : updateSelected,
              onCategoryUpdate,
              onReassignClick: (c) => onReassignClick([c]),
            }),
        }),
        {},
      ),
    [
      selected,
      isSelectedMode,
      isReassignEnabled,
      isShiftPressed,
      onSelect,
      updateSelected,
      onCategoryUpdate,
      onReassignClick,
    ],
  );

  const tableHeaderContentTransformations = useMemo(
    () => ({
      checkbox: () => (
        <Checkbox
          checked={isAllSelected}
          onChange={() => (isAllSelected ? removeAll() : selectAll())}
        />
      ),
    }),
    [isAllSelected, removeAll, selectAll],
  );

  return (
    <>
      {isSelectedMode && (
        <RfiCategoriesActionMenu
          selected={selected}
          onCategoryUpdate={onCategoryUpdate}
          onReassignClick={() => onReassignClick(selected)}
          onClose={removeAll}
        />
      )}

      <ReorderResizeTable
        className={classes}
        isDragDisabled
        isResizeDisabled
        isDataLoading={isLoading}
        columns={tableColumns}
        data={data as unknown as RfiSpreadsheetCategoryTableRow[]}
        headingTranslations={HEADING_TRANSLATIONS}
        tableContentTransformations={tableContentTransformations}
        tableHeaderContentTransformations={tableHeaderContentTransformations}
        sortingValues={SORTING_VALUES}
        customSizes={CUSTOM_COL_SIZES}
        totalPages={totalPages}
        totalItems={totalItems}
        onRowClick={(row) => onRowClick(row as unknown as DataRoomRFICategory)}
      />

      <RfiReassignCategoriesModal
        ref={reassignModalRef}
        categories={reassignCategories}
        assignedEmails={assignedEmails}
        onCategoryUpdate={onCategoryUpdate}
        onReassigned={removeAll}
      />
    </>
  );
};

export default RfiSpreadsheetCategoriesTable;
